import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { PageCriteriaModelInterface } from '../../../../../../Common/Misc/models/page-criteria.model.interface';
import { TargetingSearch } from '../../../../models/targeting-search.model';
import { TargetingSearchesApiService } from '../../../../../../api/targeting/targeting-searches.api.service';
import { TargetingResultModel } from '../../../../models/targeting-result.model';
import { TargetingListsApiService } from '../../../../../../api/targeting/targeting-lists.api.service';
import { LocalizedEnum } from '../../../../../../api/journodex/model/localized-enum';
import { FilterTypeModel } from '../../../../../../Common/Filter/models/abstract-filter-type.model';
import { TargetingSearchResponseApiModel } from '../../../../../../api/targeting/model/targeting-search-response.api.model';
import { JournodexSubjectsService } from '../../../../../../Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../../../../Core/services/journodex-enums.service';
import { LocalizationService } from '../../../../../../Core/Localization/services/localization.service';
import { BaseSearchService, TargetingFacetCollection } from '../../../../services/base-search.service';
import { JournodexRegionsService } from '../../../../../../Common/Journodex/services/journodex-regions.service';
import { SortFieldEnum } from '../../../../../../Common/TargetingShared/enum/SortField.enum';
import { TargetingResultFactory } from '../../../../factories/targeting-result.factory';
import { DistributionRecipientModel } from '../../../../models/distribution-recipient-model';
import { IdNamePairModel } from '../../../../../../Common/Misc/models/id-name-pair.model';
import { promiseOnlyResolved } from '../../../../../../Core/utils';
import { ExcludedRecipientApiModel } from '../../../../../../api/distributions/model/excluded-recipient.api.model';
import { RecipientEditorService } from '../recipient-editor.service';
import { CurrentUserStateService } from '../../../../../../Core/Users/services/current-user-state.service';
import { EntityType } from '../../../../../../api/targeting/enum/entity.type.enum';
import { TargetingSearchesService } from '@Discover/Targeting/services/targeting-searches.service';
import { DistributionApi } from '@api/distributions/distribution.api';
import { SearchModeEnum } from '../../../../../../Common/TargetingShared/enum/search-mode.enum';
import { FilterSectionEnum } from '../../../../../../Common/Filter/enums/filter-section.enum';

@Injectable()
export class RecipientViewerService extends BaseSearchService {

    private primaryContact: TargetingResultModel = null;
    private primaryContact$: BehaviorSubject<TargetingResultModel> = new BehaviorSubject<TargetingResultModel>(this.primaryContact);

    protected nRecipients$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    protected nResults$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    private nContactExclusions: number = 0;
    protected nContactExclusions$: BehaviorSubject<number> = new BehaviorSubject<number>(this.nContactExclusions);
    private nOutletExclusions: number = 0;
    protected nOutletExclusions$: BehaviorSubject<number> = new BehaviorSubject<number>(this.nOutletExclusions);

    contactSortFields: LocalizedEnum[] = [
        {
            enumValue: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            displayString: 'Discover.targeting.sortFields.uniqueVisitors'
        },
        {
            enumValue: SortFieldEnum.FIRST_NAME,
            displayString: 'Discover.targeting.sortFields.firstName'
        },
        {
            enumValue: SortFieldEnum.LAST_NAME,
            displayString: 'Discover.targeting.sortFields.lastName'
        },
        {
            enumValue: SortFieldEnum.JOB_TITLE,
            displayString: 'Discover.targeting.sortFields.jobTitle'
        },
        {
            enumValue: SortFieldEnum.OUTLET_NAME,
            displayString: 'Discover.targeting.sortFields.outletName'
        },
        {
            enumValue: SortFieldEnum.COUNTRY_NAME,
            displayString: 'Common.country'
        },
        {
            enumValue: SortFieldEnum.PROVINCE_NAME,
            displayString: 'Common.province'
        },
        {
            enumValue: SortFieldEnum.CITY_NAME,
            displayString: 'Common.city'
        }
    ];

    search: TargetingSearch;
    excludedRecipients: ExcludedRecipientApiModel[];
    viewExclusions: boolean = false;
    exclusionEntityType: EntityType = EntityType.CONTACT;
    hasContactExclusions: boolean = false;
    hasOutletExclusions: boolean = false;
    cachedFilters: TargetingSearch;

    initialRecipientTotalSet = false;

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingSearchesService: TargetingSearchesService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingResultFactory: TargetingResultFactory,
        protected recipientEditorService: RecipientEditorService,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionApiService: DistributionApi
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApiService
        );
        this.setFilters(this.allFilters);

        this.targetingSortFields$.next(this.contactSortFields);
        this.sortField = this.contactSortFields[0];
        this.sortField$.next(this.contactSortFields[0]);

        this.subscriptions.push(
            this.recipientEditorService.getExcludedRecipients().subscribe(x => {
                this.excludedRecipients = x;
                this.hasContactExclusions = this.excludedRecipients.some(exclusion => !!exclusion.contactId);
                this.hasOutletExclusions = this.excludedRecipients.some(exclusion => !!exclusion.outletId);
            })
        );
    }

    getFilterDefinitions(): FilterTypeModel[] {
        const filters: FilterTypeModel[] = [
            this.audienceReachFilter,
            this.subjectFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.languageFilter,
            this.locationFilter,
            this.contactNameFilter,
            this.statusFilter,
            this.targetAudienceRegionFilter,
            this.outletNameFilter,
        ];

        if (this.search?.searchMode === SearchModeEnum.outlet) {
            this.outletNameFilter.section = FilterSectionEnum.OUTLET_DETAIL;
        } else {
            this.outletNameFilter.section = FilterSectionEnum.CONTACT_DETAIL;
        }
        // TODO: add aoc and list filter, alphabetically. Backend not implemented at the moment

        return filters;
    }

    /**
     * Updates the page criteria
     */
    setPageCriteria(pageCriteria: PageCriteriaModelInterface, search: boolean = true): void {
        this.page = pageCriteria.page;
        if (this.page <= this.totalNumberOfPages && search) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            this.doSearch();
        }
    }

    /**
     * Checks that the page criteria can be updated
     * @NOTE This is an observable so that users can make the decision via dialogs etc.
     */
    canSetPageCriteria(): Observable<boolean> {
        return of(true);
    }

    public doSearch(refreshPrimaryFacets: boolean = false, refreshFilteredFacets: boolean = false): Observable<TargetingSearchResponseApiModel> {
        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        let mainSearchRequest: Observable<TargetingSearchResponseApiModel>;

        const search = this.search?.clone();
        search.list = this.search.list;
        if (this.viewExclusions) {
            if (this.exclusionEntityType === EntityType.CONTACT) {
                search.outletIdsNames.include = [];
                search.contactIdsNames.include = this.excludedRecipients
                    .map(x => x.contactId)
                    .filter(x => !!x)
                    .map(x => new IdNamePairModel(null, x));
            } else {
                search.contactIdsNames.include = [];
                search.outletIdsNames.include = this.excludedRecipients
                    .map(x => x.outletId)
                    .filter(x => !!x)
                    .map(x => new IdNamePairModel(null, x));
            }
            mainSearchRequest = this.fastSearch(search, filters);
        } else {
            mainSearchRequest = this.fastSearch(search, filters);
        }

        // just to make sure it runs, in case the caller doesn't subscribe
        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.subscribe(
            () => {
                this.mainSearchRequest = null;
                this.searchLoading$.next(false);
                // this.setSearchState(SearchStatesEnum.success);
            },
            (err) => {
                console.log(err);
                // this.setSearchState(SearchStatesEnum.error, err);
            }
        );

        let filteredFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshFilteredFacets) {
            if (this.filteredFacetRequest) {
                this.filteredFacetRequest.unsubscribe();
            }
            filteredFacetRequest = this.facetSearch(search, filters);

            this.filteredFacetRequest = filteredFacetRequest.subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        let primaryFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshPrimaryFacets) {
            if (this.primaryFacetRequest) {
                this.primaryFacetRequest.unsubscribe();
            }
            if (isFiltered || !filteredFacetRequest) {
                primaryFacetRequest = this.facetSearch(search);
            } else {
                primaryFacetRequest = filteredFacetRequest;
            }
            this.primaryFacetRequest = primaryFacetRequest.subscribe(collection => {
                this.setPrimaryFacets(collection);
                this.primaryFacetRequest = null;
            });
        }

        return mainSearchRequest;
    }

    // Fast search is just the results without any facets
    fastSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<TargetingSearchResponseApiModel> {
        const rows = this.pageCriteria.pageSize;
        const start = (this.pageCriteria.page - 1) * rows;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        const searchRequest: Observable<TargetingSearchResponseApiModel> = this.targetingSearchesApiService.performSearch(
            search.toApiModel(false, filters).query,
            start,
            rows,
            sortOrder,
            sortField
        );

        return searchRequest.pipe(
            switchMap(async response => {
                await this.setSearchResults(response);
                return response;
            })
        );
    }

    // Override to compute the recipient count once
    async setSearchResults(response: TargetingSearchResponseApiModel): Promise<void> {
        // This will happen before any filtering has occured
        if (!this.initialRecipientTotalSet) {
            this.nRecipients$.next(response.numResults);
            this.initialRecipientTotalSet = true;
        }
        this.nResults = response.numResults;
        this.nResults$.next(this.nResults);
        const resultPromises = response.results.map(x => this.targetingResultFactory.createTargetingResult(x));
        this.results = await promiseOnlyResolved(resultPromises);
        this.results$.next(this.results);
        this.fetchListMemberships();
        this.searchLoading$.next(false);
    }

    setRecipient(recipient: DistributionRecipientModel) {
        if (recipient.targetingSearchId) {
            this.targetingSearchesService.getById(recipient.targetingSearchId).subscribe(search => {
                this.search = search;
                this.doSearch(true, true);
                this.getExclusionTotals();
            });
        } else if (recipient.targetingListId) {
            this.search = new TargetingSearch();
            this.search.list.include = [recipient.targetingListId];
            if (recipient.searchMode) {
                this.search.searchMode = recipient.searchMode;
            }
            this.doSearch(true, true);
            this.getExclusionTotals();
        } else if (recipient.portfolioId) {
            this.search = new TargetingSearch();
            this.search.list.include = [recipient.portfolioId];
            this.doSearch(true, true);
            this.getExclusionTotals();
        }
    }

    getExclusionTotals() {
        this.nContactExclusions = 0;
        this.nOutletExclusions = 0;
        const rows = this.pageCriteria.pageSize;
        const start = (this.pageCriteria.page - 1) * rows;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        if (this.hasContactExclusions) {
            const contactFilters = this.generateFiltersSearch();
            contactFilters.contactIdsNames.include = this.excludedRecipients
                .map(x => x.contactId)
                .filter(x => !!x)
                .map(x => new IdNamePairModel(null, x));
            this.targetingSearchesApiService.performSearch(
                this.search.toApiModel(false, contactFilters).query,
                start,
                rows,
                sortOrder,
                sortField
            ).pipe(
                take(1)
            ).subscribe(contactExclusions => {
                if (contactExclusions.numResults) {
                    this.nContactExclusions = contactExclusions.numResults;
                    this.nContactExclusions$.next(this.nContactExclusions);
                }
            });
        } else {
            this.nContactExclusions$.next(this.nContactExclusions);
        }

        if (this.hasOutletExclusions) {
            const outletFilters = this.generateFiltersSearch();
            outletFilters.outletIdsNames.include = this.excludedRecipients
                .map(x => x.outletId)
                .filter(x => !!x)
                .map(x => new IdNamePairModel(null, x));
            this.targetingSearchesApiService.performSearch(
                this.search.toApiModel(false, outletFilters).query,
                start,
                rows,
                sortOrder,
                sortField
            ).pipe(
                take(1)
            ).subscribe(outletExclusions => {
                if (outletExclusions) {
                    this.nOutletExclusions = outletExclusions.numResults;
                    this.nOutletExclusions$.next(this.nOutletExclusions);
                }
            });
        } else {
            this.nOutletExclusions$.next(this.nOutletExclusions);
        }
    }

    clearSearch(): void {
        this.nResults = 0;
        this.nResults$.next(this.nResults);
        this.results = null;
        this.results$.next(this.results);
        this.searchLoading$.next(false);

        this.totalNumberOfPages = 0;
        this.setPageCriteria({
            page: 1,
            pageSize: 25
        }, false);
    }

    addExclusion(recipient: DistributionRecipientModel, entity: TargetingResultModel) {
        this.recipientEditorService.addExclusion(recipient, entity);
        if (entity.entityType === EntityType.CONTACT) {
            this.nContactExclusions++;
            this.nContactExclusions$.next(this.nContactExclusions);
        } else {
            this.nOutletExclusions++;
            this.nOutletExclusions$.next(this.nOutletExclusions);
        }
    }

    removeExclusion(recipient: DistributionRecipientModel, entity: TargetingResultModel) {
        this.recipientEditorService.removeExclusion(recipient, entity);
        if (entity.entityType === EntityType.CONTACT) {
            this.nContactExclusions--;
            this.nContactExclusions$.next(this.nContactExclusions);
        } else {
            this.nOutletExclusions--;
            this.nOutletExclusions$.next(this.nOutletExclusions);
        }
    }

    getRecipientTotal() {
        return this.nRecipients$;
    }

    getFilteredResultTotal() {
        return this.nResults$;
    }

    getContactExclusionsTotal() {
        return this.nContactExclusions$;
    }

    getOutletExclusionsTotal() {
        return this.nOutletExclusions$;
    }
}
